import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { mockArticleDetails, relatedArticles } from '@/mocks/articleDetail';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fontSize, setFontSize] = useState<'sm' | 'md' | 'lg'>('md');

  const article = mockArticleDetails.find((a) => a.id === id) ?? mockArticleDetails[0];

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const el = document.documentElement;
      const scrollTop = el.scrollTop || document.body.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      setReadProgress(scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fontSizeMap = { sm: 'text-sm', md: 'text-base', lg: 'text-lg' };
  const bodyFontSize = fontSizeMap[fontSize];

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d7ao4ef6e3lk9704a1ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter']">

      {/* ── READ PROGRESS BAR ── */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-[#E0E0E0]">
        <div
          className="h-full bg-[#C41230] transition-all duration-100"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      {/* ── NAVBAR ── */}
      <header className={`fixed top-0.5 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white border-b border-[#E0E0E0]' : 'bg-white border-b border-[#E0E0E0]'}`}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="w-8 h-8 flex items-center justify-center border border-[#E0E0E0] hover:border-[#111111] hover:bg-[#111111] hover:text-white transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/cb3f25066178f57a7b4ff6ec1e98d674/3adf4e78d4ccc80f9408185bfae7ead8.png"
                alt="Jardel City Post"
                className="h-8 w-auto object-contain"
              />
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-6">
            {['Política', 'Cidade', 'Economia', 'Cultura', 'Esportes'].map((item) => (
              <Link
                key={item}
                to="/"
                className="text-[10px] font-bold uppercase tracking-widest text-[#333333] hover:text-[#C41230] transition-colors whitespace-nowrap"
              >
                {item}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Font size controls */}
            <div className="hidden md:flex items-center gap-0 border border-[#E0E0E0]">
              {(['sm', 'md', 'lg'] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-all cursor-pointer whitespace-nowrap ${fontSize === size ? 'bg-[#111111] text-white' : 'text-[#555555] hover:bg-[#F0F0F0]'}`}
                >
                  {size === 'sm' ? 'A' : size === 'md' ? 'A+' : 'A++'}
                </button>
              ))}
            </div>
            <button
              onClick={() => setSubscribeOpen(true)}
              className="text-[10px] font-bold uppercase tracking-widest px-4 py-2 bg-[#C41230] text-white hover:bg-[#A00E25] transition-all whitespace-nowrap cursor-pointer"
            >
              Assinar
            </button>
          </div>
        </div>
      </header>

      {/* ── ARTICLE HEADER ── */}
      <div className="pt-14">
        {/* Category + breadcrumb */}
        <div className="border-b border-[#E0E0E0]">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2">
            <Link to="/" className="text-[10px] text-[#999999] hover:text-[#C41230] font-['Source_Serif_4'] transition-colors">
              Início
            </Link>
            <span className="text-[10px] text-[#CCCCCC]">/</span>
            <span
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: article.categoryColor }}
            >
              {article.category}
            </span>
          </div>
        </div>

        {/* Hero image — full width */}
        <div className="relative w-full overflow-hidden" style={{ maxHeight: '520px' }}>
          <img
            src={article.image}
            alt={article.title}
            className="w-full object-cover object-top"
            style={{ height: '520px' }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          {/* Caption overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-6 py-4">
            <p className="text-[10px] text-white/70 font-['Source_Serif_4'] max-w-4xl mx-auto">{article.imageCaption}</p>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-12">

          {/* ── LEFT: Article body ── */}
          <article>
            {/* Category label */}
            <div className="flex items-center gap-3 mb-4">
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
                style={{ color: article.categoryColor, borderColor: article.categoryColor }}
              >
                {article.category}
              </span>
              <span className="text-[10px] text-[#999999] font-['Source_Serif_4']">{article.edition}</span>
            </div>

            {/* Title */}
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#111111] leading-[1.15] mb-5">
              {article.title}
            </h1>

            {/* Subtitle */}
            <p className="font-['Source_Serif_4'] text-lg text-[#444444] leading-relaxed border-l-4 border-l-[#C41230] pl-4 mb-6">
              {article.subtitle}
            </p>

            {/* Meta row */}
            <div className="flex flex-wrap items-center gap-4 py-4 border-y border-[#E0E0E0] mb-8">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 overflow-hidden border border-[#E0E0E0] flex-shrink-0">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-xs font-bold text-[#111111] font-sans">{article.author}</p>
                  <p className="text-[10px] text-[#999999] font-['Source_Serif_4']">{article.authorRole}</p>
                </div>
              </div>
              <div className="h-4 w-px bg-[#E0E0E0] hidden sm:block"></div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-[#999999] text-xs"></i>
                </div>
                <span className="text-[11px] text-[#666666] font-['Source_Serif_4']">{article.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-[#999999] text-xs"></i>
                </div>
                <span className="text-[11px] text-[#666666] font-['Source_Serif_4']">{article.readTime} de leitura</span>
              </div>
              {/* Share buttons */}
              <div className="ml-auto flex items-center gap-1.5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#999999] mr-1">Compartilhar</span>
                {[
                  { icon: 'ri-twitter-x-line', label: 'X' },
                  { icon: 'ri-facebook-line', label: 'Facebook' },
                  { icon: 'ri-whatsapp-line', label: 'WhatsApp' },
                  { icon: 'ri-link', label: 'Copiar link' },
                ].map((s) => (
                  <button
                    key={s.label}
                    title={s.label}
                    className="w-7 h-7 flex items-center justify-center border border-[#E0E0E0] hover:border-[#111111] hover:bg-[#111111] hover:text-white transition-all cursor-pointer"
                  >
                    <i className={`${s.icon} text-xs`}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Body text — 2 column editorial layout on large screens */}
            <div className={`font-['Source_Serif_4'] ${bodyFontSize} text-[#222222] leading-relaxed`}>
              {article.body.map((block, i) => {
                if (block.type === 'paragraph') {
                  return (
                    <p key={i} className="mb-5 text-[#333333] leading-[1.85]">
                      {block.text}
                    </p>
                  );
                }
                if (block.type === 'subheading') {
                  return (
                    <h2
                      key={i}
                      className="font-['Playfair_Display'] text-xl font-bold text-[#111111] mt-8 mb-4 border-b border-[#E0E0E0] pb-2"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'quote') {
                  return (
                    <blockquote
                      key={i}
                      className="my-8 border-l-4 border-l-[#111111] pl-5 pr-4 py-2 bg-[#F8F8F8]"
                    >
                      <p className="font-['Playfair_Display'] text-lg italic text-[#111111] leading-snug">
                        {block.text}
                      </p>
                    </blockquote>
                  );
                }
                if (block.type === 'highlight') {
                  return (
                    <div
                      key={i}
                      className="my-8 p-5 border border-[#111111] bg-[#111111] text-white"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-[#C41230] text-base"></i>
                        </div>
                        <p className="font-['Source_Serif_4'] text-sm leading-relaxed text-white/90">
                          {block.text}
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-[#E0E0E0]">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#999999] mb-3">Temas relacionados</p>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 border border-[#CCCCCC] text-[#555555] hover:border-[#111111] hover:text-[#111111] transition-all cursor-pointer whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Author bio box */}
            <div className="mt-8 p-5 border border-[#E0E0E0] bg-[#F8F8F8]">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 overflow-hidden border border-[#E0E0E0] flex-shrink-0">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#C41230] mb-0.5">Sobre o autor</p>
                  <p className="font-['Playfair_Display'] text-base font-bold text-[#111111]">{article.author}</p>
                  <p className="text-[10px] text-[#999999] font-sans mb-2">{article.authorRole}</p>
                  <p className="text-xs text-[#555555] font-['Source_Serif_4'] leading-relaxed">{article.authorBio}</p>
                </div>
              </div>
            </div>

            {/* Share bottom */}
            <div className="mt-8 pt-6 border-t-2 border-[#111111] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs font-bold uppercase tracking-widest text-[#111111]">Gostou desta matéria? Compartilhe.</p>
              <div className="flex items-center gap-2">
                {[
                  { icon: 'ri-twitter-x-line', label: 'X' },
                  { icon: 'ri-facebook-line', label: 'Facebook' },
                  { icon: 'ri-whatsapp-line', label: 'WhatsApp' },
                ].map((s) => (
                  <button
                    key={s.label}
                    className="flex items-center gap-1.5 px-3 py-2 border border-[#E0E0E0] hover:border-[#111111] hover:bg-[#111111] hover:text-white transition-all cursor-pointer text-[10px] font-bold uppercase tracking-wide whitespace-nowrap"
                  >
                    <i className={`${s.icon} text-xs`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </article>

          {/* ── RIGHT: Sidebar ── */}
          <aside className="space-y-0">

            {/* Subscribe CTA */}
            <div className="border border-[#111111] mb-6">
              <div className="bg-[#111111] px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#C41230]">Jardel City Post</p>
                <p className="font-['Playfair_Display'] text-base font-bold text-white leading-snug mt-0.5">
                  Jornalismo independente há 40 anos.
                </p>
              </div>
              <div className="p-4">
                <p className="text-xs text-[#555555] font-['Source_Serif_4'] leading-relaxed mb-4">
                  Acesse conteúdo exclusivo, edições impressas e análises aprofundadas. Assine e apoie o jornalismo de verdade.
                </p>
                <button
                  onClick={() => setSubscribeOpen(true)}
                  className="w-full py-2.5 bg-[#C41230] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#A00E25] transition-all cursor-pointer whitespace-nowrap"
                >
                  Assinar agora
                </button>
                <Link
                  to="/"
                  className="block w-full mt-2 py-2.5 border border-[#111111] text-[#111111] text-[10px] font-bold uppercase tracking-widest hover:bg-[#111111] hover:text-white transition-all cursor-pointer whitespace-nowrap text-center"
                >
                  Ler mais notícias
                </Link>
              </div>
            </div>

            {/* Edition info */}
            <div className="border border-[#E0E0E0] mb-6">
              <div className="border-b-2 border-[#111111] px-4 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#111111]">Esta edição</p>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#999999] font-['Source_Serif_4'] uppercase tracking-wide">Edição</span>
                  <span className="text-[10px] font-bold text-[#111111]">Nº 14.820</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#999999] font-['Source_Serif_4'] uppercase tracking-wide">Data</span>
                  <span className="text-[10px] font-bold text-[#111111]">7 Abr 2026</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#999999] font-['Source_Serif_4'] uppercase tracking-wide">Editoria</span>
                  <span
                    className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: article.categoryColor }}
                  >
                    {article.category}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#999999] font-['Source_Serif_4'] uppercase tracking-wide">Leitura</span>
                  <span className="text-[10px] font-bold text-[#111111]">{article.readTime}</span>
                </div>
              </div>
            </div>

            {/* Other articles in same category */}
            <div className="border border-[#E0E0E0] mb-6">
              <div className="border-b-2 border-[#111111] px-4 py-2.5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#111111]">Mais em {article.category}</p>
              </div>
              <div className="divide-y divide-[#E0E0E0]">
                {mockArticleDetails
                  .filter((a) => a.id !== article.id)
                  .slice(0, 3)
                  .map((a) => (
                    <Link
                      key={a.id}
                      to={`/article/${a.id}`}
                      className="flex gap-3 p-3 hover:bg-[#FAFAFA] transition-colors group cursor-pointer"
                    >
                      <div className="w-16 h-16 overflow-hidden flex-shrink-0 border border-[#E0E0E0]">
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className="text-[9px] font-bold uppercase tracking-widest"
                          style={{ color: a.categoryColor }}
                        >
                          {a.category}
                        </span>
                        <p className="font-['Playfair_Display'] text-xs font-bold text-[#111111] leading-snug mt-0.5 group-hover:text-[#C41230] transition-colors line-clamp-3">
                          {a.title}
                        </p>
                        <p className="text-[9px] text-[#999999] mt-1 font-['Source_Serif_4']">{a.date}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>

            {/* QR Code box */}
            <div className="border border-[#E0E0E0] p-4 bg-[#F8F8F8]">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-qr-code-line text-[#C41230] text-xl"></i>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#111111] mb-1">Edição impressa</p>
                  <p className="text-[10px] text-[#666666] font-['Source_Serif_4'] leading-relaxed">
                    Esta matéria também está disponível na edição impressa. Escaneie o QR code para acessar o conteúdo digital complementar.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ── RELATED ARTICLES ── */}
      <section className="border-t-2 border-[#111111] bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-end justify-between mb-8 border-b border-[#E0E0E0] pb-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#C41230] mb-1">Continue lendo</p>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#111111]">Artigos relacionados</h2>
            </div>
            <Link
              to="/"
              className="text-[10px] font-bold uppercase tracking-widest text-[#C41230] hover:underline whitespace-nowrap cursor-pointer"
            >
              Ver todas as notícias →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#E0E0E0]">
            {relatedArticles.map((rel, i) => (
              <Link
                key={rel.id}
                to={`/article/${rel.id}`}
                className={`bg-white group cursor-pointer hover:bg-[#FAFAFA] transition-colors ${i < 2 ? 'border-r border-[#E0E0E0]' : ''}`}
              >
                <div className="overflow-hidden border-b border-[#E0E0E0]">
                  <img
                    src={rel.image}
                    alt={rel.title}
                    className="w-full h-44 object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="text-[9px] font-bold uppercase tracking-widest"
                      style={{ color: rel.categoryColor }}
                    >
                      {rel.category}
                    </span>
                    <span className="text-[9px] text-[#CCCCCC]">·</span>
                    <span className="text-[9px] text-[#999999] font-['Source_Serif_4']">{rel.date}</span>
                    <span className="text-[9px] text-[#CCCCCC]">·</span>
                    <span className="text-[9px] text-[#999999] font-['Source_Serif_4']">{rel.readTime}</span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-base font-bold text-[#111111] leading-snug mb-2 group-hover:text-[#C41230] transition-colors">
                    {rel.title}
                  </h3>
                  <p className="text-[11px] text-[#666666] font-['Source_Serif_4'] leading-relaxed line-clamp-2">
                    {rel.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-[#C41230]">
                    <span className="text-[10px] font-bold uppercase tracking-widest">Ler matéria</span>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-[#F0F0F0] border-t-2 border-[#111111]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/cb3f25066178f57a7b4ff6ec1e98d674/3adf4e78d4ccc80f9408185bfae7ead8.png"
                alt="Jardel City Post"
                className="h-8 w-auto object-contain"
              />
              <div className="h-4 w-px bg-[#CCCCCC]"></div>
              <p className="text-[10px] text-[#888888] font-['Source_Serif_4']">
                <strong className="text-[#444444]">Jornalista Renato Silva</strong> · MTB 32945
              </p>
            </div>
            <div className="flex items-center gap-4">
              {['Política de Privacidade', 'Termos de Uso', 'Anuncie'].map((link) => (
                <a key={link} href="#" rel="nofollow" className="text-[10px] text-[#888888] hover:text-[#C41230] font-['Source_Serif_4'] transition-colors whitespace-nowrap">
                  {link}
                </a>
              ))}
            </div>
            <p className="text-[10px] text-[#AAAAAA] font-['Source_Serif_4']">© 2026 Jardel City Post</p>
          </div>
        </div>
      </footer>

      {/* ── SUBSCRIBE MODAL ── */}
      {subscribeOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setSubscribeOpen(false); setSubmitted(false); }}></div>
          <div className="relative bg-white w-full max-w-md border-t-4 border-t-[#C41230]">
            <div className="border-b-2 border-[#111111] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#C41230]">Assinatura</p>
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#111111]">Assinar Jardel City Post</h3>
              </div>
              <button
                onClick={() => { setSubscribeOpen(false); setSubmitted(false); }}
                className="w-8 h-8 flex items-center justify-center border border-[#E0E0E0] hover:border-[#111111] hover:bg-[#111111] hover:text-white transition-all cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            {submitted ? (
              <div className="p-8 text-center">
                <div className="w-12 h-12 bg-[#16A34A]/10 border border-[#16A34A] flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-[#16A34A] text-xl"></i>
                </div>
                <h4 className="font-['Playfair_Display'] text-xl font-bold text-[#111111] mb-2">Assinatura recebida!</h4>
                <p className="font-['Source_Serif_4'] text-sm text-[#555555] leading-relaxed">
                  Obrigado! Nossa equipe entrará em contato em breve para confirmar os detalhes.
                </p>
                <button
                  onClick={() => { setSubscribeOpen(false); setSubmitted(false); }}
                  className="mt-6 px-6 py-2.5 bg-[#111111] text-white text-[10px] font-bold uppercase tracking-widest hover:bg-[#333333] transition-all cursor-pointer whitespace-nowrap"
                >
                  Fechar
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubscribe} className="p-6 space-y-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#555555] mb-1.5">Nome completo</label>
                  <input name="nome" type="text" required placeholder="Seu nome" className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] focus:outline-none focus:border-[#111111] font-['Source_Serif_4']" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#555555] mb-1.5">E-mail</label>
                  <input name="email" type="email" required placeholder="seu@email.com" className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] focus:outline-none focus:border-[#111111] font-['Source_Serif_4']" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-[#555555] mb-1.5">Tipo de assinatura</label>
                  <select name="tipo_assinatura" required className="w-full px-3 py-2.5 text-sm border border-[#CCCCCC] focus:outline-none focus:border-[#111111] cursor-pointer bg-white font-['Source_Serif_4']">
                    <option value="">Selecione...</option>
                    <option value="digital">Digital — Acesso online completo</option>
                    <option value="impresso">Impresso — Entrega semanal em casa</option>
                    <option value="combo">Combo — Digital + Impresso</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 bg-[#C41230] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#A00E25] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </div>
                      Enviando...
                    </>
                  ) : 'Confirmar assinatura'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
